import React from 'react';
import { Box, Typography } from '@mui/material';
import DepthBadge from '../DepthBadge';
import { depthMoveParts, depthReading } from '../depthReading';

// DWLF-416: the briefing's weekly depth section, from `crossAsset.depth` (GET /briefing/daily?depth=1). IN ZONE
// gets a row each, the loud reading; shallow / deep / beyond share one quiet line; not reached is left out, since
// nothing in the band applies yet. A read that failed says so rather than looking like an empty book.

const MONO = '"IBM Plex Mono", monospace';
const INTO = { low: 'into the low', high: 'into the high' };

const MAX_NAMED = 8;
const named = (symbols) => {
  const list = Array.isArray(symbols) ? symbols : [];
  if (!list.length) return '';
  const more = list.length > MAX_NAMED ? ` +${list.length - MAX_NAMED} more` : '';
  return `: ${list.slice(0, MAX_NAMED).join(', ')}${more}`;
};

function failureLine({ read, failed, failedSymbols }) {
  if (!failed) return null;
  if (!read) return 'Depth unavailable today.';
  return `Depth unavailable for ${failed} of ${read + failed} weekly windows${named(failedSymbols)}.`;
}

// The API reads the first `symbolLimit` symbols of the book; the rest are named, never shown as quiet.
function omittedLine({ omitted, omittedSymbols, symbolLimit }) {
  if (!(omitted > 0)) return null;
  const limit = Number.isFinite(symbolLimit) ? ` (depth is read for the first ${symbolLimit})` : '';
  return `Depth not read for ${omitted} more symbol${omitted === 1 ? '' : 's'}${limit}${named(omittedSymbols)}.`;
}

export default function BriefingDepth({ depth }) {
  const readings = Array.isArray(depth?.readings) ? depth.readings : [];
  // Split by the shared vocabulary's tones: loud is IN ZONE, muted is shallow / deep / beyond, quiet (not reached) is left out.
  const toned = readings.map((r) => ({ ...r, tone: depthReading(r.depth, r.side)?.tone }));
  const inZone = toned.filter((r) => r.tone === 'loud');
  const others = toned.filter((r) => r.tone === 'muted');
  const failed = depth ? failureLine(depth) : null;
  const omitted = depth ? omittedLine(depth) : null;
  if (!inZone.length && !others.length && !failed && !omitted) return null;

  return (
    <Box data-testid="briefing-depth" sx={{ mt: 3, pt: 3, borderTop: '1px solid var(--dwlf-ink-06)' }}>
      <Typography sx={{ fontFamily: MONO, fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--dwlf-ink-40)', mb: 0.75 }}>
        Weekly cycle depth
      </Typography>
      <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', color: 'var(--dwlf-ink-50)', lineHeight: 1.6, mb: 1.5, maxWidth: 520 }}>
        Where the last close sits in the move past weekly cycles made into each window. IN ZONE is inside the
        typical move (25th–75th percentile). Historical context, not a target.
      </Typography>

      {inZone.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: others.length || failed || omitted ? 1.5 : 0 }}>
          {inZone.map(({ symbol, side, depth: d }) => {
            const { moved, typical, sample } = depthMoveParts(d, side);
            const sub = [moved, typical && sample ? `${typical} (${sample})` : typical].filter(Boolean).join(' · ');
            return (
              <Box key={`${symbol}|${side}`} data-testid="briefing-depth-in-zone" sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                <DepthBadge depth={d} side={side} />
                <Typography sx={{ fontFamily: MONO, fontSize: '0.8rem', fontWeight: 600, color: 'var(--dwlf-text)' }}>{symbol}</Typography>
                <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', color: 'var(--dwlf-ink-60)' }}>{INTO[side]}</Typography>
                {sub && (
                  <Typography sx={{ fontFamily: MONO, fontSize: '0.65rem', color: 'var(--dwlf-ink-45)', flexBasis: { xs: '100%', sm: 'auto' } }}>
                    {sub}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {others.length > 0 && (
        <Box data-testid="briefing-depth-others" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography sx={{ fontFamily: MONO, fontSize: '0.65rem', color: 'var(--dwlf-ink-40)' }}>Also:</Typography>
          {others.map(({ symbol, side, depth: d }) => (
            <Box key={`${symbol}|${side}`} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <DepthBadge depth={d} side={side} />
              <Typography sx={{ fontFamily: MONO, fontSize: '0.65rem', color: 'var(--dwlf-ink-55)' }}>
                {symbol} ({side})
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {failed && (
        <Typography data-testid="briefing-depth-failed" sx={{ fontFamily: MONO, fontSize: '0.65rem', color: 'var(--dwlf-warn)', mt: 1 }}>
          {failed}
        </Typography>
      )}

      {omitted && (
        <Typography data-testid="briefing-depth-omitted" sx={{ fontFamily: MONO, fontSize: '0.65rem', color: 'var(--dwlf-ink-45)', mt: 1 }}>
          {omitted}
        </Typography>
      )}
    </Box>
  );
}
